import { Address, MLS } from '@aloft/toolkit-shared';
import { Block, Flex, Grid, IconButton, Input, RadioGroup, Select } from '@aloft/toolkit-sui';
// eslint-disable-next-line aloft/no-sui-chakra
import { Input as ChakraInput } from '@aloft/toolkit-sui/chakra';

import { useCallback } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { useGoogleMapsLoader } from '../../../../../hooks/googleMaps/useGoogleMapsLoader.ts';
import { AddComparablesForm, CompEntryType } from '../types.ts';

export const CompEntryInput = ({
  index,
  method,
  onMethodChange,
  onRemove,
}: {
  index: number;
  method: CompEntryType;
  onMethodChange: (index: number, method: CompEntryType) => void;
  onRemove: (index: number) => void;
}) => {
  const mapsLoader = useGoogleMapsLoader();
  const { control, setValue } = useFormContext<AddComparablesForm>();

  const fieldName = `entries.${index}` as const;

  const handlePlaceChange = useCallback(
    (address: Address) => {
      setValue(
        fieldName,
        {
          method: 'address',
          AddressLineText: address.AddressLineText ?? '',
          unitNumber: address.unitNumber ?? '',
          CityName: address.CityName ?? '',
          StateCode: address.StateCode ?? '',
          PostalCode: address.PostalCode ?? '',
        },
        { shouldValidate: true, shouldDirty: true, shouldTouch: true },
      );
    },
    [fieldName, setValue],
  );

  const mlsOptions = MLS._keys
    .sort((a, b) => MLS[a].shortName.localeCompare(MLS[b].shortName))
    .filter((key) => MLS[key].isActive)
    .map((key) => ({ label: MLS[key].shortName, value: key }));

  return (
    <Flex gap={3} flexDirection="column">
      <Flex gap={2} alignItems="flex-start" position="relative">
        <Flex flexDirection="column" gap={1} flex={1}>
          <RadioGroup
            label="Select the method in which you wish to add comps:"
            size="lg"
            forceHorizontal
            columnGap="2rem"
            items={[
              { label: 'MLS ID', value: 'mls' },
              { label: 'Street Address', value: 'address' },
            ]}
            value={method}
            onChange={(value) => onMethodChange(index, value as 'mls' | 'address')}
          />
        </Flex>
        {index > 0 && (
          <IconButton
            size="sm"
            variant="tertiary"
            __dangerDoNotUseSx={{ position: 'absolute', right: '0', top: '0', zIndex: 1 }}
            icon="delete-bin"
            ariaLabel="Remove entry"
            onClick={() => onRemove(index)}
          />
        )}
      </Flex>

      {method === 'mls' ? (
        <Flex gap={2}>
          <Controller
            control={control}
            name={`${fieldName}.mls`}
            render={({ field }) => (
              <Select
                options={[{ label: '', value: '' }, ...mlsOptions]}
                {...field}
                onChange={(val) => field.onChange(val ?? '')}
                label="MLS"
                isRequired
              />
            )}
          />
          <Controller
            control={control}
            name={`${fieldName}.mlsListingID`}
            render={({ field }) => (
              <Input
                type="text"
                {...field}
                label="MLS Listing ID"
                onClear={() => field.onChange('')}
              />
            )}
          />
        </Flex>
      ) : (
        <Grid gridTemplateColumns="3fr 1fr 1.4fr" gap={2} alignItems="end">
          <Block __dangerDoNotUseSx={{ gridColumn: 'span 2 / span 2' }}>
            <Controller
              name={`${fieldName}.AddressLineText`}
              control={control}
              render={({ field, fieldState }) => (
                <Input
                  {...field}
                  isRequired
                  label="Comp Address"
                  type="locationAddressAutocomplete"
                  mapsLoader={mapsLoader}
                  onClear={() => field.onChange('')}
                  isInvalid={fieldState.invalid}
                  onPlaceChanged={handlePlaceChange}
                />
              )}
            />
          </Block>
          <Block>
            <Controller
              name={`${fieldName}.unitNumber`}
              control={control}
              render={({ field, fieldState }) => (
                <ChakraInput
                  {...field}
                  type="text"
                  placeholder="Suite/Apt #"
                  isInvalid={fieldState.invalid}
                />
              )}
            />
          </Block>
          <Block>
            <Controller
              name={`${fieldName}.CityName`}
              control={control}
              render={({ field, fieldState }) => (
                <ChakraInput
                  {...field}
                  isRequired
                  type="text"
                  placeholder="City"
                  isInvalid={fieldState.invalid}
                />
              )}
            />
          </Block>
          <Block>
            <Controller
              name={`${fieldName}.StateCode`}
              control={control}
              render={({ field, fieldState }) => (
                <ChakraInput
                  {...field}
                  isRequired
                  type="text"
                  placeholder="State"
                  isInvalid={fieldState.invalid}
                />
              )}
            />
          </Block>
          <Block>
            <Controller
              name={`${fieldName}.PostalCode`}
              control={control}
              render={({ field, fieldState }) => (
                <ChakraInput
                  {...field}
                  isRequired
                  type="text"
                  placeholder="Zip"
                  isInvalid={fieldState.invalid}
                />
              )}
            />
          </Block>
        </Grid>
      )}
    </Flex>
  );
};
